import { api, API_BASE } from '../../core/network/api';

/**
 * The real-money top-up surface — Shop's "Get Gems" section and Settings' Purchases/Invoices
 * tabs. Wire shapes below are pinned to the live backend (`GET :3401`), not to the earlier
 * placeholder contract this file started against — every field here was checked against a
 * running server, not guessed.
 */

/** One entry of `GET /topup/packages`'s `packages` array. `tag` is a short marketing label
 * ("Best value", "+10% coins") — `null` when the package carries none; `best` is a highlight
 * flag, not a price or a bonus amount, so it is never rendered as a number. */
export interface TopupPackage {
  id: string;
  name: string;
  priceCents: number;
  coins: number;
  tag: string | null;
  best: boolean;
}

/** `custom` (buy-any-amount terms) is part of the live contract but unused here — every caller
 * only ever offers the fixed `packages` — kept typed so a future custom-amount UI has the shape
 * ready rather than re-deriving it from the network tab. */
export interface TopupCustomTerms {
  minCoins: number;
  maxCoins: number;
  centsPerCoin: number;
}

export interface TopupPackagesView {
  packages: TopupPackage[];
  /** True only while the Mock payment provider is active — never assume it, always read it:
   * it is what gates the "no real charge is made" note on the Shop's Get Coins section. */
  testMode: boolean;
  /** ISO 4217 currency code prices are denominated in (e.g. 'EUR', 'PLN'). Falls back to
   * 'EUR' when the backend predates multicurrency. */
  currency?: string;
  custom: TopupCustomTerms;
}

/** `GET /topup/packages`. */
export const fetchTopupPackages = () => api.get<TopupPackagesView>('/topup/packages');

export interface CheckoutResult {
  /** A hosted-checkout URL for a real provider; the Mock provider still returns one (its own
   * confirmation page), which is why this is unconditional rather than optional. */
  url: string;
  /** True only for the Mock provider, which settles inline. A real gateway answers `false` and
   * the caller must redirect to `url` — the credit lands only after that provider's callback. */
  completed: boolean;
  coins: number;
  /** The checkout session's id — what `fetchTopupSessionStatus` polls once the player is back
   * from the gateway. Optional on the wire for an older backend; the provider also appends it
   * to the return URL as `?session=`, so the Shop has two places to read it from. */
  sessionId?: string;
}

/** `GET /topup/session/:id/status`. `pending` until the gateway's webhook has credited the gems,
 * then `completed`; `expired` when the session's window closed without one. `coins` is what the
 * checkout promised, so the Shop can say "+1,000 gems" without a second request. */
export interface TopupSessionStatus {
  status: 'pending' | 'completed' | 'expired';
  coins: number;
}

export const fetchTopupSessionStatus = (sessionId: string) =>
  api.get<TopupSessionStatus>(`/topup/session/${encodeURIComponent(sessionId)}/status`);

/** Where the Shop parks the session id across the hop to the gateway and back — the return URL
 * carries it too, and this is the fallback for a gateway that drops the query string. */
export const PAY_SESSION_STORAGE_KEY = 'bl_pay_session';

/** `POST /topup/checkout` — buys one package by id. */
export const topupCheckout = (packageId: string) =>
  api.post<CheckoutResult>('/topup/checkout', { packageId });

/** `POST /topup/checkout` with a custom gem amount — priced server-side at `custom.centsPerCoin`,
 * bounded by `custom.minCoins..maxCoins`; the server rejects anything outside with a 400. */
export const topupCheckoutCustom = (coins: number) =>
  api.post<CheckoutResult>('/topup/checkout', { coins });

/** One `GET /topup/history` row — the real-money purchase ledger, separate from
 * `wallet/ledger`'s SOFT/PREMIUM currency ledger. `pack` is the package's own display name at
 * the time of purchase (not a live join against the current catalogue, so a renamed or
 * discontinued package still reads correctly on an old row). */
export interface PurchaseEntry {
  id: string;
  pack: string;
  coins: number;
  amountCents: number;
  status: string;
  createdAt: string;
}

/** `GET /topup/history` — this player's coin purchases, newest first. */
export const fetchTopupHistory = () => api.get<PurchaseEntry[]>('/topup/history');

/** One `GET /topup/invoices` row. `downloadUrl` is read defensively as possibly absent even
 * though the live contract always sends it, so an invoice mid-generation never renders a
 * Download button pointed at nothing. */
export interface InvoiceEntry {
  invoiceNumber: string;
  description: string;
  totalCents: number;
  currency: string;
  status: string;
  date: string;
  downloadUrl?: string | null;
}

/** `GET /topup/invoices` — this player's invoices, newest first. Preview instances answer this
 * with a 503 (`PreviewDisabledGuard` — invoices are one of preview's disabled features per
 * CLAUDE.md), which the Invoices tab renders as its own notice rather than a retry card. */
export const fetchInvoices = () => api.get<InvoiceEntry[]>('/topup/invoices');

/**
 * Fetches an invoice PDF and hands it to the browser as a download.
 *
 * A plain `<a href target="_blank">` would navigate outside the axios client entirely, so a
 * 401 (the 15-minute access cookie expired) would just show the browser a raw JSON error page
 * instead of being repaired by the refresh interceptor — the same trap Fimbulwake's Invoices
 * tab hit and fixed (`engine/game/topup-api.ts`). This mirrors that fix: a real `fetch` (the
 * only way to read the response as a `Blob` rather than JSON), a 401 handled explicitly by
 * refreshing through `api` — the same rotation the axios interceptor performs — and a retry.
 * Anything still failing is thrown for the caller to show inline.
 */
export async function downloadInvoicePdf(invoiceNumber: string): Promise<void> {
  const url = `${API_BASE}/topup/invoices/${encodeURIComponent(invoiceNumber)}/download`;
  const hit = () => fetch(url, { credentials: 'include', headers: { Accept: 'application/pdf' } });

  let res = await hit();
  if (res.status === 401) {
    await api.post('/auth/refresh');
    res = await hit();
  }
  if (!res.ok) throw new Error(`invoice download failed (${res.status})`);

  // Re-wrapped with an explicit type rather than handed straight from `res.blob()`: the save is
  // driven by the blob's own content type, and a proxy that answers without one leaves the
  // browser guessing at a file it has already been told the name of.
  const blob = new Blob([await res.arrayBuffer()], { type: 'application/pdf' });
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = `${invoiceNumber}.pdf`;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.appendChild(a);
  // A NON-BUBBLING click, and this is the whole bug: `main.ts` delegates every click on the
  // document to the SPA router (`document.addEventListener('click', …)` → `closest('a[href]')`
  // → `e.preventDefault(); navigate(a.pathname)`). A `blob:` URL created from this page reports
  // the page's own origin, so the router accepted the synthetic anchor as an internal link —
  // it cancelled the download with `preventDefault()` and navigated the shell to the blob's
  // UUID, which resolves to nothing and lands the player back on `/play`. Measured in Chromium:
  // the request answered 200 with 2,251 bytes of `application/pdf`, no `download` event ever
  // fired, and the Settings overlay closed itself. `a.click()` bubbles; this does not, so the
  // delegate never sees it and the browser performs the save.
  a.dispatchEvent(new MouseEvent('click', { bubbles: false, cancelable: true }));
  // The anchor STAYS ATTACHED, and this is the bug that made "invoices don't download" real
  // while the endpoint answered 200 with 2,251 bytes of PDF the whole time: `a.click()` only
  // *queues* the download, and Chromium cancels a queued download whose initiating element has
  // already left the document. Removing the anchor on the next line raced the save every time —
  // no `download` event, no file, and no error for the UI to show, which is exactly what a
  // player reported. It is removed with the blob instead, once the browser has had the save.
  window.setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(href);
  }, 10_000);
}
